import type { CalendarPost, CalendarRolloutDay, CalendarSong, GetPromoCalendarResponse } from '../types';
import { dayKey } from './calendarDates';
import { CalendarFilterValues, passesFilters } from './calendarFilterModel';

/** What one day of the grid holds. */
export interface CalendarDayContent {
  /** The posts that pass the filters, in time order. */
  posts: CalendarPost[];
  /** The songs that come out on this day (the release band). */
  releases: CalendarSong[];
  /** The rollout steps of this day, except the release day itself. */
  steps: CalendarRolloutDay[];
}

export const EMPTY_DAY: CalendarDayContent = { posts: [], releases: [], steps: [] };

/** The day key of a post, in the local time of the browser. */
export function postDayKey(post: Pick<CalendarPost, 'scheduledAt'>): string {
  return dayKey(new Date(post.scheduledAt));
}

function byTime(a: CalendarPost, b: CalendarPost): number {
  const byScheduledAt = a.scheduledAt.localeCompare(b.scheduledAt);
  return byScheduledAt !== 0 ? byScheduledAt : a.platform.localeCompare(b.platform);
}

function songPasses(catalogItemUuid: string, filters: CalendarFilterValues): boolean {
  return filters.songs.length === 0 || filters.songs.includes(catalogItemUuid);
}

/**
 * The content of each day, by day key. A filter keeps the release bands and the rollout steps
 * (they are context, not posts); only the song filter hides the bands of the other songs (a3).
 */
export function buildCalendarDays(
  data: GetPromoCalendarResponse | undefined,
  filters: CalendarFilterValues
): Map<string, CalendarDayContent> {
  const days = new Map<string, CalendarDayContent>();
  if (data === undefined) {
    return days;
  }
  const dayOf = (key: string): CalendarDayContent => {
    const existing = days.get(key);
    if (existing !== undefined) {
      return existing;
    }
    const created: CalendarDayContent = { posts: [], releases: [], steps: [] };
    days.set(key, created);
    return created;
  };

  [...data.posts]
    .filter((post) => passesFilters(post, filters))
    .sort(byTime)
    .forEach((post) => dayOf(postDayKey(post)).posts.push(post));

  data.songs
    .filter((song) => song.releaseDate !== null && songPasses(song.catalogItemUuid, filters))
    .forEach((song) => dayOf(song.releaseDate as string).releases.push(song));

  data.rolloutDays
    .filter((step) => songPasses(step.catalogItemUuid, filters))
    .filter((step) => !(step.kind === 'release' && step.dayKey === 'outNow'))
    .forEach((step) => dayOf(step.date).steps.push(step));

  return days;
}

/** The songs by catalog item uuid. */
export function songsByUuid(songs: CalendarSong[] | undefined): Map<string, CalendarSong> {
  return new Map((songs ?? []).map((song) => [song.catalogItemUuid, song]));
}

/** The posts that wait for the artist, oldest first (the Needs you strip). */
export function postsNeedingYou(posts: CalendarPost[] | undefined): CalendarPost[] {
  return (posts ?? []).filter((post) => post.status === 'awaitingManualPost').sort(byTime);
}
